'use strict'

const KMZ_STRAIGHT_LINE_SCOPE_PRIORITY = {
  playback: ['waylines', 'template'],
  templateExport: ['template', 'waylines'],
  waylinesExport: ['waylines', 'template']
}

const KMZ_STRAIGHT_LINE_ROOT_CACHE_PATHS = [
  'wpml:globalUseStraightLine',
  'globalUseStraightLine',
  'Folder.wpml:globalUseStraightLine',
  'Folder.globalUseStraightLine',
  'wpml:useStraightLine',
  'useStraightLine'
]

const KMZ_STRAIGHT_LINE_SCOPED_CACHE_PATHS = [
  'wpml:globalUseStraightLine',
  'globalUseStraightLine',
  'Folder.wpml:globalUseStraightLine',
  'Folder.globalUseStraightLine',
  'wpml:useStraightLine',
  'useStraightLine'
]

const fnCloneStraightLineValue = function (value) {
  if (Array.isArray(value)) {
    return value.map(fnCloneStraightLineValue)
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = fnCloneStraightLineValue(value[key])
    })
    return out
  }
  return value
}

const fnGetByPath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return undefined
    current = current[parts[i]]
  }
  return current
}

const fnSetByPath = function (obj, path, value) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  let current = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
      current[key] = {}
    }
    current = current[key]
  }
  current[parts[parts.length - 1]] = fnCloneStraightLineValue(value)
}

const fnDeletePath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  const stack = [{ parent: null, key: '', value: obj }]
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return
    const key = parts[i]
    if (!Object.prototype.hasOwnProperty.call(current, key)) return
    current = current[key]
    stack.push({ parent: stack[stack.length - 1].value, key: key, value: current })
  }
  const leaf = stack.pop()
  if (!leaf || !leaf.parent || !leaf.key) return
  delete leaf.parent[leaf.key]
  while (stack.length > 1) {
    const node = stack.pop()
    if (!node || !node.parent || !node.key) continue
    const value = node.parent[node.key]
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length) break
    delete node.parent[node.key]
  }
}

const fnPickFirstDefined = function (source, paths) {
  const safeSource = (source && typeof source === 'object') ? source : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const path = String(list[i] || '').trim()
    if (!path) continue
    const value = fnGetByPath(safeSource, path)
    if (value === undefined) continue
    return value
  }
  return undefined
}

const fnAssignScopedStraightLineSource = function (target, targetPath, source, sourcePaths) {
  const value = fnPickFirstDefined(source, sourcePaths)
  if (value === undefined) return
  fnSetByPath(target, targetPath, value)
}

const fnBuildScopeStraightLineSource = function (pointProps, folderProps) {
  const out = {}
  fnAssignScopedStraightLineSource(out, 'wpml:useStraightLine', pointProps, [
    'wpml:useStraightLine',
    'useStraightLine'
  ])
  fnAssignScopedStraightLineSource(out, 'Folder.wpml:globalUseStraightLine', pointProps, [
    'Folder.wpml:globalUseStraightLine',
    'Folder.globalUseStraightLine',
    'wpml:globalUseStraightLine',
    'globalUseStraightLine'
  ])
  fnAssignScopedStraightLineSource(out, 'Folder.wpml:globalUseStraightLine', folderProps, [
    'wpml:globalUseStraightLine',
    'globalUseStraightLine',
    'Folder.wpml:globalUseStraightLine',
    'Folder.globalUseStraightLine'
  ])
  return out
}

const fnPruneKmzStraightLineCache = function (props) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return
  KMZ_STRAIGHT_LINE_ROOT_CACHE_PATHS.forEach((path) => {
    fnDeletePath(safeProps, path)
  })
  ;['template', 'waylines'].forEach((scopeId) => {
    KMZ_STRAIGHT_LINE_SCOPED_CACHE_PATHS.forEach((path) => {
      fnDeletePath(safeProps, scopeId + '.' + path)
    })
  })
}

const buildKmzScopedStraightLinePropsFromSources = function ({
  templatePointProps,
  waylinePointProps,
  templateFolderProps,
  waylinesFolderProps
} = {}) {
  return {
    template: fnBuildScopeStraightLineSource(templatePointProps, templateFolderProps),
    waylines: fnBuildScopeStraightLineSource(waylinePointProps, waylinesFolderProps)
  }
}

const applyKmzScopedStraightLineProps = function (props, scopedStraightLineProps) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return safeProps
  const scoped = (scopedStraightLineProps && typeof scopedStraightLineProps === 'object') ? scopedStraightLineProps : {}
  fnPruneKmzStraightLineCache(safeProps)
  ;['template', 'waylines'].forEach((scopeId) => {
    const scopeProps = scoped[scopeId]
    if (!scopeProps || typeof scopeProps !== 'object') return
    fnAssignScopedStraightLineSource(safeProps, scopeId + '.wpml:useStraightLine', scopeProps, [
      'wpml:useStraightLine',
      'useStraightLine'
    ])
    fnAssignScopedStraightLineSource(safeProps, scopeId + '.Folder.wpml:globalUseStraightLine', scopeProps, [
      'Folder.wpml:globalUseStraightLine',
      'Folder.globalUseStraightLine',
      'wpml:globalUseStraightLine',
      'globalUseStraightLine'
    ])
  })
  return safeProps
}

const syncKmzPointStraightLineScopesFromXml = function (point, {
  fnXmlToObj,
  templateFolderProps,
  waylinesFolderProps,
  templateFolderEl,
  waylinesFolderEl
} = {}) {
  if (!point || typeof fnXmlToObj !== 'function') return point
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const templatePointProps = point.xmlEl ? fnXmlToObj(point.xmlEl) : {}
  const waylinePointEl = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const waylinePointProps = waylinePointEl ? fnXmlToObj(waylinePointEl) : {}
  const scoped = buildKmzScopedStraightLinePropsFromSources({
    templatePointProps: templatePointProps,
    waylinePointProps: waylinePointProps,
    templateFolderProps: templateFolderProps || (templateFolderEl ? fnXmlToObj(templateFolderEl) : {}),
    waylinesFolderProps: waylinesFolderProps || (waylinesFolderEl ? fnXmlToObj(waylinesFolderEl) : {})
  })
  applyKmzScopedStraightLineProps(point.props, scoped)
  return point
}

const syncKmzMissionStraightLineScopesFromXml = function (missionState, fnXmlToObj) {
  if (!missionState || !Array.isArray(missionState.waypoints) || typeof fnXmlToObj !== 'function') return missionState
  const templateFolderProps = missionState.folderEl ? fnXmlToObj(missionState.folderEl) : {}
  const waylinesFolderProps = missionState.secondaryFolderEl ? fnXmlToObj(missionState.secondaryFolderEl) : {}
  missionState.waypoints.forEach((point) => {
    syncKmzPointStraightLineScopesFromXml(point, {
      fnXmlToObj: fnXmlToObj,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    })
  })
  return missionState
}

const fnNormalizeStraightLinePathForCheck = function (path) {
  let normalized = String(path || '').trim()
  if (!normalized) return ''
  const lower = normalized.toLowerCase()
  if (lower.indexOf('template.') === 0) normalized = normalized.slice(9)
  if (String(normalized).toLowerCase().indexOf('waylines.') === 0) normalized = normalized.slice(9)
  return String(normalized || '').trim().toLowerCase()
}

const isKmzStraightLineFieldPath = function (path) {
  const normalized = fnNormalizeStraightLinePathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:globalusestraightline' ||
    normalized === 'globalusestraightline' ||
    normalized === 'folder.wpml:globalusestraightline' ||
    normalized === 'folder.globalusestraightline' ||
    normalized === 'wpml:usestraightline' ||
    normalized === 'usestraightline'
}

const isKmzGlobalStraightLineFieldPath = function (path) {
  const normalized = fnNormalizeStraightLinePathForCheck(path)
  if (!normalized) return false
  return normalized === 'wpml:globalusestraightline' ||
    normalized === 'globalusestraightline' ||
    normalized === 'folder.wpml:globalusestraightline' ||
    normalized === 'folder.globalusestraightline'
}

const fnResolveStraightLineValueByUseCase = function (pointOrProps, useCase, mode, fallbackValue) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const scopePriority = KMZ_STRAIGHT_LINE_SCOPE_PRIORITY[useCase] || KMZ_STRAIGHT_LINE_SCOPE_PRIORITY.playback
  for (let i = 0; i < scopePriority.length; i += 1) {
    const scopeId = scopePriority[i]
    let paths = []
    if (mode === 'local') {
      paths = [
        scopeId + '.wpml:useStraightLine',
        scopeId + '.useStraightLine'
      ]
    } else if (mode === 'global') {
      paths = [
        scopeId + '.Folder.wpml:globalUseStraightLine',
        scopeId + '.Folder.globalUseStraightLine',
        scopeId + '.wpml:globalUseStraightLine',
        scopeId + '.globalUseStraightLine'
      ]
    }
    for (let j = 0; j < paths.length; j += 1) {
      const value = fnGetByPath(props, paths[j])
      if (value === undefined || value === null) continue
      if (typeof value === 'string' && !String(value).trim()) continue
      return value
    }
  }
  return fallbackValue
}

const resolveKmzPlaybackUseStraightLine = function (pointOrProps, fallbackValue) {
  return fnResolveStraightLineValueByUseCase(pointOrProps, 'playback', 'local', fallbackValue)
}

const resolveKmzPlaybackGlobalUseStraightLine = function (pointOrProps, fallbackValue) {
  return fnResolveStraightLineValueByUseCase(pointOrProps, 'playback', 'global', fallbackValue)
}

const resolveKmzTemplateExportUseStraightLine = function (pointOrProps, fallbackValue) {
  return fnResolveStraightLineValueByUseCase(pointOrProps, 'templateExport', 'local', fallbackValue)
}

const resolveKmzTemplateExportGlobalUseStraightLine = function (pointOrProps, fallbackValue) {
  return fnResolveStraightLineValueByUseCase(pointOrProps, 'templateExport', 'global', fallbackValue)
}

const resolveKmzWaylinesExportUseStraightLine = function (pointOrProps, fallbackValue) {
  return fnResolveStraightLineValueByUseCase(pointOrProps, 'waylinesExport', 'local', fallbackValue)
}

const resolveKmzWaylinesExportGlobalUseStraightLine = function (pointOrProps, fallbackValue) {
  return fnResolveStraightLineValueByUseCase(pointOrProps, 'waylinesExport', 'global', fallbackValue)
}

const applyKmzResolvedStraightLineDisplayValuesToMap = function (map, point, useCase) {
  const targetMap = (map && typeof map === 'object') ? map : {}
  const safeUseCase = String(useCase || 'playback').trim()
  const globalValue = fnResolveStraightLineValueByUseCase(point, safeUseCase, 'global', undefined)
  const localValue = fnResolveStraightLineValueByUseCase(point, safeUseCase, 'local', undefined)
  if (globalValue !== undefined) {
    targetMap['wpml:globalUseStraightLine'] = globalValue
  }
  if (localValue !== undefined) {
    targetMap['wpml:useStraightLine'] = localValue
  }
  return targetMap
}

export {
  buildKmzScopedStraightLinePropsFromSources,
  applyKmzScopedStraightLineProps,
  syncKmzPointStraightLineScopesFromXml,
  syncKmzMissionStraightLineScopesFromXml,
  isKmzStraightLineFieldPath,
  isKmzGlobalStraightLineFieldPath,
  resolveKmzPlaybackUseStraightLine,
  resolveKmzPlaybackGlobalUseStraightLine,
  resolveKmzTemplateExportUseStraightLine,
  resolveKmzTemplateExportGlobalUseStraightLine,
  resolveKmzWaylinesExportUseStraightLine,
  resolveKmzWaylinesExportGlobalUseStraightLine,
  applyKmzResolvedStraightLineDisplayValuesToMap
}
